import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { vehicleService } from '../../services/vehicleService';
import { carrierService } from '../../services/carrierService';
import {
  PencilIcon,
  CalendarIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [carrier, setCarrier] = useState(null);
  const [vehicleType, setVehicleType] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    fetchVehicleData();
  }, [id]);

  const fetchVehicleData = async () => {
    setIsLoading(true);
    try {
      const vehicleResponse = await vehicleService.getVehicleById(id);
      if (vehicleResponse && vehicleResponse.success && vehicleResponse.data) {
        setVehicle(vehicleResponse.data);

        // Taşıyıcı bilgilerini al
        if (vehicleResponse.data.carrier_id) {
          fetchCarrierData(vehicleResponse.data.carrier_id);
        }

        // Araç tipi bilgilerini al
        if (vehicleResponse.data.vehicle_type_id) {
          fetchVehicleTypeData(vehicleResponse.data.vehicle_type_id);
        }
      } else {
        throw new Error('Araç bilgileri alınamadı');
      }
    } catch (error) {
      toast.error('Araç bilgileri yüklenirken bir hata oluştu');
      navigate('/vehicles');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCarrierData = async (carrierId) => {
    try {
      const carrierResponse = await carrierService.getCarrierById(carrierId);
      if (carrierResponse && carrierResponse.success && carrierResponse.data) {
        setCarrier(carrierResponse.data);
      }
    } catch (error) {
      console.error('Error fetching carrier:', error);
    }
  };

  const fetchVehicleTypeData = async (typeId) => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response && response.success && response.data) {
        const foundType = response.data.vehicleTypes.find(type => type.id === parseInt(typeId));
        if (foundType) {
          setVehicleType(foundType);
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle type:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);

    if (tab === 'transfers' && transfers.length === 0) {
      fetchTransfers();
    }
  };

  const fetchTransfers = async () => {
    try {
      // Bu kısım backend'de henüz implement edilmemiş olabilir
      // const response = await vehicleService.getVehicleTransfers(id);
      // if (response && response.success && response.data) {
      //   setTransfers(response.data);
      // }

      // Şimdilik boş bir dizi kullanıyoruz
      setTransfers([]);
    } catch (error) {
      console.error('Error fetching transfers:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-500">Araç bulunamadı.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">{vehicle.plate_number}</h1>
        <div className="mt-3 flex sm:mt-0 sm:ml-4">
          <Link
            to={`/vehicles/${id}/edit`}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PencilIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Düzenle
          </Link>
        </div>
      </div>

      {/* Sekmeler */}
      <div className="mt-6">
        <div className="sm:hidden">
          <label htmlFor="tabs" className="sr-only">
            Sekme seçin
          </label>
          <select
            id="tabs"
            name="tabs"
            className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
          >
            <option value="details">Detaylar</option>
            <option value="transfers">Transferler</option>
          </select>
        </div>
        <div className="hidden sm:block">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8" aria-label="Tabs">
              <button
                onClick={() => handleTabChange('details')}
                className={`${
                  activeTab === 'details'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
              >
                Detaylar
              </button>
              <button
                onClick={() => handleTabChange('transfers')}
                className={`${
                  activeTab === 'transfers'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <CalendarIcon className={`-ml-0.5 mr-2 h-5 w-5 ${
                  activeTab === 'transfers' ? 'text-primary-500' : 'text-gray-400'
                }`} />
                Transferler
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Sekme İçeriği */}
      <div className="mt-6">
        {activeTab === 'details' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Araç Bilgileri</h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">Araç detayları ve sürücü bilgileri.</p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Plaka</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vehicle.plate_number}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Taşıyıcı</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {carrier ? (
                      <Link to={`/carriers/${carrier.id}`} className="text-primary-600 hover:text-primary-900">
                        {carrier.name}
                      </Link>
                    ) : 'Yükleniyor...'}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Araç Tipi</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {vehicleType ? `${vehicleType.name} (Kapasite: ${vehicleType.capacity})` : 'Yükleniyor...'}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Sürücü Adı</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vehicle.driver_name || '-'}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Sürücü Telefonu</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{vehicle.driver_phone || '-'}</dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Sürücü Değerlendirmesi</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex items-center">
                      <div className="flex items-center">
                        {[0, 1, 2, 3, 4].map((rating) => (
                          <StarIcon
                            key={rating}
                            className={`${
                              vehicle.driver_rating > rating ? 'text-yellow-400' : 'text-gray-300'
                            } h-5 w-5 flex-shrink-0`}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                      <p className="ml-2">{vehicle.driver_rating} / 5</p>
                    </div>
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Durum</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      vehicle.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {vehicle.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                  </dd>
                </div>
                {vehicle.vehicle_photo && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Araç Fotoğrafı</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="mt-2">
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${vehicle.vehicle_photo}`}
                          alt="Araç Fotoğrafı"
                          className="max-w-xs rounded-md shadow-sm"
                        />
                      </div>
                    </dd>
                  </div>
                )}
                {vehicle.driver_photo && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Sürücü Fotoğrafı</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      <div className="mt-2">
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${vehicle.driver_photo}`}
                          alt="Sürücü Fotoğrafı"
                          className="max-w-xs rounded-md shadow-sm"
                        />
                      </div>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        )}

        {activeTab === 'transfers' && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Transferler</h3>
            </div>
            {transfers.length === 0 ? (
              <div className="text-center p-12">
                <p className="text-gray-500">Bu araca ait transfer bulunamadı.</p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {transfers.map((transfer) => (
                  <li key={transfer.id}>
                    <Link to={`/transfers/${transfer.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-primary-600 truncate">
                            Transfer #{transfer.id}
                          </p>
                          <div className="ml-2 flex-shrink-0 flex">
                            <p className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              transfer.status === 'completed' ? 'bg-green-100 text-green-800' :
                              transfer.status === 'passenger_on_board' ? 'bg-purple-100 text-purple-800' :
                              transfer.status === 'arrived_at_pickup' ? 'bg-orange-100 text-orange-800' :
                              transfer.status === 'on_the_way_to_pickup' ? 'bg-yellow-100 text-yellow-800' :
                              transfer.status === 'vehicle_assigned' ? 'bg-indigo-100 text-indigo-800' :
                              transfer.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              transfer.status === 'pending' ? 'bg-gray-100 text-gray-800' :
                              transfer.status === 'cancelled' || transfer.status === 'cancelled_by_customer' || transfer.status === 'cancelled_by_operator' || transfer.status === 'cancelled_by_driver' ? 'bg-red-100 text-red-800' :
                              transfer.status === 'no_show' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {transfer.status === 'completed' ? 'Tamamlandı' :
                               transfer.status === 'passenger_on_board' ? 'Yolcu Alındı' :
                               transfer.status === 'arrived_at_pickup' ? 'Alış Noktasında' :
                               transfer.status === 'on_the_way_to_pickup' ? 'Yolda' :
                               transfer.status === 'vehicle_assigned' ? 'Araç Atandı' :
                               transfer.status === 'confirmed' ? 'Onaylandı' :
                               transfer.status === 'pending' ? 'Beklemede' :
                               transfer.status === 'cancelled' ? 'İptal Edildi' :
                               transfer.status === 'cancelled_by_customer' ? 'Müşteri İptali' :
                               transfer.status === 'cancelled_by_operator' ? 'Operatör İptali' :
                               transfer.status === 'cancelled_by_driver' ? 'Şoför İptali' :
                               transfer.status === 'no_show' ? 'Gelmedi' :
                               transfer.status}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              Tarih: {new Date(transfer.transfer_date).toLocaleDateString('tr-TR')}
                            </p>
                            <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                              Saat: {transfer.transfer_time}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default VehicleDetail;
